import { createPortal } from 'react-dom';
import { useEffect, type ReactNode } from 'react';

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  size?: 'default' | 'sm' | 'lg' | 'xl';
  closeOnBackdrop?: boolean;
}

const SIZE_CLASSES: Record<NonNullable<FormModalProps['size']>, string> = {
  default: '',
  sm: 'modal-sm',
  lg: 'modal-lg',
  xl: 'modal-xl',
};

export function FormModal({ isOpen, onClose, title, children, size = 'default', closeOnBackdrop = false }: FormModalProps) {
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="modal modal-blur show position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }}
      tabIndex={-1}
      role="dialog"
      onClick={closeOnBackdrop ? onClose : undefined}>
      <div
        className={`modal-dialog modal-dialog-centered ${SIZE_CLASSES[size]}`}
        style={{ minWidth: '50%' }}
        role="document"
        onClick={(e) => e.stopPropagation()}>
        <div className="modal-content" style={{ maxHeight: 'calc(100vh - 3rem)' }}>
          <div className="modal-header">
            <h5 className="modal-title text-break">{title}</h5>
            <button type="button" className="btn-close flex-shrink-0" onClick={onClose} aria-label="Close" />
          </div>
          <div className="overflow-auto" style={{ minHeight: 0 }}>
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
